import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { CURRENCIES } from '../../utils/currencies';

interface ExchangeRate {
  id: number;
  currency_code: string;
  rate: string;
  source: string;
  synced_at: string | null;
}

interface RateForm {
  currency_code: string;
  rate: string;
}

const emptyForm: RateForm = { currency_code: '', rate: '' };

export function ExchangeRates() {
  const confirmDelete = useConfirm();
  const { showSuccess, showError } = useToast();
  const [items, setItems] = useState<ExchangeRate[] | null>(null);
  const [editing, setEditing] = useState<'new' | ExchangeRate | null>(null);
  const [form, setForm] = useState<RateForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get<ExchangeRate[]>('/exchange-rates').then((res) => setItems(res.data));

  useEffect(() => {
    load();
  }, []);

  const trackedCodes = new Set((items ?? []).map((item) => item.currency_code));
  const availableCurrencies = CURRENCIES.filter((c) => c.code !== 'VUV' && !trackedCodes.has(c.code));

  const openNew = () => {
    setForm({ currency_code: availableCurrencies[0]?.code ?? '', rate: '' });
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: ExchangeRate) => {
    setForm({ currency_code: item.currency_code, rate: item.rate });
    setErrors({});
    setEditing(item);
  };

  const set = (key: keyof RateForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/exchange-rates', form);
      else if (editing) await api.put(`/exchange-rates/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: ExchangeRate) => {
    if (!(await confirmDelete(`Stop tracking ${item.currency_code}?`))) return;
    try {
      await api.delete(`/exchange-rates/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post<{ message: string }>('/exchange-rates/sync');
      showSuccess(res.data.message);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <div>
          <h1>Exchange Rates</h1>
          <p className="muted">Base currency: VUV</p>
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={sync} disabled={syncing || items?.length === 0}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
          <button type="button" className="btn btn-primary" onClick={openNew} disabled={availableCurrencies.length === 0}>
            + Add
          </button>
        </div>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Currency</th>
                <th>1 VUV =</th>
                <th>Source</th>
                <th>Last synced</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.currency_code}</strong>
                  </td>
                  <td>{item.rate}</td>
                  <td>
                    <span className={`badge ${item.source === 'api' ? 'badge-active' : 'badge-neutral'}`}>
                      {item.source === 'api' ? 'Synced' : 'Manual'}
                    </span>
                  </td>
                  <td className="muted">{item.synced_at ? new Date(item.synced_at).toLocaleString() : '—'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No currencies tracked yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add currency' : 'Edit rate'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Currency
              {editing === 'new' ? (
                <select value={form.currency_code} onChange={set('currency_code')}>
                  {availableCurrencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input value={form.currency_code} disabled />
              )}
              {errors.currency_code && <small className="error">{errors.currency_code[0]}</small>}
            </label>
            <label className="span-2">
              Rate (1 VUV = ? {form.currency_code || 'currency'})
              <input type="number" step="0.000001" min="0" value={form.rate} onChange={set('rate')} required />
              {errors.rate && <small className="error">{errors.rate[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
